import { Box } from "@chakra-ui/react"
import React, {
  useCallback, useMemo, useRef, useState,
} from "react"
import { useDrop } from "react-dnd"
import { BadgeElementType } from "sharedTypes/badge"
import GridOverlay from "components/CanvasEditor/GridOverlay"
import { useContentScale, useGetViewport } from "components/CanvasEditor/Editor"
import ObjectLayer, { LayerObject } from "components/CanvasEditor/ObjectLayer"
import { getTemplateDimensions } from "../utils/badgeHelpers"
import { formatMeasurement } from "../utils/units"
import BadgeObject from "./BadgeObject"
import BadgeElementContextMenu from "./BadgeElementContextMenu"
import { canLeaveBadge, OVERFLOW_EXTENT } from "../utils/constants"
import { useBadgeElement } from "../contexts/BadgeElementContext"
import { useBadgeUI } from "../contexts/BadgeUIContext"
import { useBadgeTemplate } from "../hooks/useBadgeTemplate"
import { useBadgeInlineEditing } from "../contexts/BadgeSelectionContext"
import { useBadgeData } from "../contexts/BadgeDataContext"
import EmptyState from "./EmptyState"

const CANVAS_SHADOW = "0 0 0 1px rgba(0, 0, 0, 0.12), 0 2px 8px -2px rgba(0, 0, 0, 0.15)"

type ContextMenuAt = { id: string; x: number; y: number }

/**
 * The badge on the editor canvas: the white badge, its elements, the overflow dimming, the
 * snap guides, and the drop target for elements dragged from the toolbar.
 */
const BadgeFrame = () => {
  const { currentTemplate: template } = useBadgeTemplate()
  const { setSelectedElementId: onSelectElement } = useBadgeInlineEditing()
  const { handleAddElement, transformElements } = useBadgeElement()
  const { guestData, eventData } = useBadgeData()
  const getViewport = useGetViewport()
  const scaleFactor = useContentScale()

  // Adds an element dropped at a point on screen, kept on the badge
  const onAddElement = useCallback(
    (elementType: BadgeElementType, screenX: number, screenY: number) => {
      const point = getViewport().screenToContent({ x: screenX, y: screenY })
      if (!point) return

      const { width, height } = getTemplateDimensions(template)
      handleAddElement(
        elementType,
        Math.max(0, Math.min(point.x, width - 5)),
        Math.max(0, Math.min(point.y, height - 5)),
      )
    },
    [getViewport, template, handleAddElement],
  )

  const { width: templateWidth, height: templateHeight } = getTemplateDimensions(template)
  const canvasWidth = templateWidth * scaleFactor
  const canvasHeight = templateHeight * scaleFactor

  const [{ isOver, dragItemType }, drop] = useDrop(
    () => ({
      accept: "TOOLBAR_ELEMENT",
      drop: (
        item: { elementType?: BadgeElementType },
        monitor,
      ) => {
        const offset = monitor.getClientOffset()
        const { elementType } = item

        if (offset && elementType) {
          onAddElement(elementType, offset.x, offset.y)
        }
      },
      collect: (monitor) => ({
        isOver: monitor.isOver(),
        dragItemType: monitor.getItemType(),
      }),
    }),
    [onAddElement],
  )
  const { showGrid, snapping, displayUnit } = useBadgeUI()
  const formatDistance = useCallback(
    (mm: number) => formatMeasurement(mm, displayUnit),
    [displayUnit],
  )

  const handleCanvasMouseDown = (e: React.MouseEvent) => {
    // Only deselect if clicking directly on canvas (not on elements)
    // Skip deselection if middle mouse button (used for panning)
    // Shift keeps the selection, so a selection box can add to it
    if (e.target === e.currentTarget && e.button !== 1 && !e.shiftKey) {
      onSelectElement("")
    }
  }

  const [contextMenu, setContextMenu] = useState<ContextMenuAt | null>(null)
  const openMenu = useRef(contextMenu)
  openMenu.current = contextMenu
  const closeContextMenu = useCallback(() => setContextMenu(null), [])

  const handleElementContextMenu = useCallback((elementId: string, e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()

    // If there's already a menu open for a different element, close it first
    const open = openMenu.current
    if (open && open.id !== elementId) {
      setContextMenu(null)
      // Use a small delay to ensure the previous menu fully closes before opening new one
      setTimeout(() => {
        setContextMenu({ id: elementId, x: e.clientX, y: e.clientY })
      }, 10)
    } else {
      // Same element or no menu open - set immediately
      setContextMenu({ id: elementId, x: e.clientX, y: e.clientY })
    }
  }, [])

  const layerObjects = useMemo<LayerObject[]>(
    () => template.badgeData.elements.map((el) => ({
      id: el.id,
      x: el.x,
      y: el.y,
      width: el.width,
      height: el.height,
      rotation: el.transforms?.rotate || 0,
      canLeaveBounds: canLeaveBadge(el.type),
    })),
    [template.badgeData.elements],
  )
  const bounds = useMemo(
    () => ({ width: templateWidth, height: templateHeight }),
    [templateWidth, templateHeight],
  )

  const shouldShowEmptyState = template.badgeData.elements.length === 0

  return (
    <Box
      ref={drop}
      id={`badge-canvas-${template.id}`}
      position="relative"
      width={`${canvasWidth}px`}
      height={`${canvasHeight}px`}
      bg={isOver ? (dragItemType === "TOOLBAR_ELEMENT" ? "green.50" : "blue.50") : "white"}
      borderRadius="none"
      outline="none"
      boxShadow={CANVAS_SHADOW}
      onMouseDown={handleCanvasMouseDown}
      data-canvas-background
      mx="auto"
      cursor="default"
    >
      <ObjectLayer
        objects={layerObjects}
        bounds={bounds}
        gridGuides={showGrid}
        snapping={snapping}
        formatDistance={formatDistance}
        onTransform={transformElements}
      >
        {template.badgeData.elements.map((element) => (
          <BadgeObject
            key={element.id}
            element={element}
            scaleFactor={scaleFactor}
            guestData={guestData}
            eventData={eventData}
            onSelect={onSelectElement}
            onContextMenu={handleElementContextMenu}
          />
        ))}
        {contextMenu && (
          <BadgeElementContextMenu
            key={`${contextMenu.id}-${contextMenu.x}-${contextMenu.y}`}
            elementId={contextMenu.id}
            x={contextMenu.x}
            y={contextMenu.y}
            onClose={closeContextMenu}
          />
        )}

        {/* Overflow dimming — shows element parts outside canvas at reduced opacity */}
        <Box
          position="absolute"
          top={`-${OVERFLOW_EXTENT}px`}
          left={`-${OVERFLOW_EXTENT}px`}
          width={`${canvasWidth + OVERFLOW_EXTENT * 2}px`}
          height={`${canvasHeight + OVERFLOW_EXTENT * 2}px`}
          bg="white"
          opacity={0.7}
          pointerEvents="none"
          zIndex={997}
          sx={{
            clipPath: `polygon(evenodd, 0 0, 100% 0, 100% 100%, 0 100%, 0 0, ${OVERFLOW_EXTENT}px ${OVERFLOW_EXTENT}px, ${OVERFLOW_EXTENT + canvasWidth}px ${OVERFLOW_EXTENT}px, ${OVERFLOW_EXTENT + canvasWidth}px ${OVERFLOW_EXTENT + canvasHeight}px, ${OVERFLOW_EXTENT}px ${OVERFLOW_EXTENT + canvasHeight}px, ${OVERFLOW_EXTENT}px ${OVERFLOW_EXTENT}px)`,
          }}
        />

        {/* Restore canvas shadow above the dimming overlay */}
        <Box
          position="absolute"
          inset={0}
          boxShadow={CANVAS_SHADOW}
          pointerEvents="none"
          zIndex={998}
        />

        <GridOverlay showGrid={showGrid} />

        {shouldShowEmptyState && (
        <EmptyState />
        )}
      </ObjectLayer>
    </Box>
  )
}

export default BadgeFrame
